import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X, Filter, SlidersHorizontal } from 'lucide-react';

export interface SessionFilters {
  phase: string;
  decisionArea: string;
  dateRange: string;
  searchQuery: string;
}

interface SessionFiltersProps {
  filters: SessionFilters;
  onFilterChange: (filters: SessionFilters) => void;
  activeFilterCount: number;
}

const PHASE_OPTIONS = [
  { value: 'all', label: 'Alle Phasen' },
  { value: '1', label: 'Phase 1 - Analyse' },
  { value: '2', label: 'Phase 2 - Optionen' },
  { value: '3', label: 'Phase 3 - Abgeschlossen' },
];

const AREA_OPTIONS = [
  { value: 'all', label: 'Alle Bereiche' },
  { value: 'strategy', label: 'Strategie & Positionierung' },
  { value: 'product', label: 'Produkt & Innovation' },
  { value: 'growth', label: 'Wachstum & Skalierung' },
  { value: 'team', label: 'Team & Führung' },
  { value: 'operations', label: 'Operations & Prozesse' },
  { value: 'finance', label: 'Finanzen & Investment' },
];

const DATE_OPTIONS = [
  { value: 'all', label: 'Gesamter Zeitraum' },
  { value: 'today', label: 'Heute' },
  { value: 'week', label: 'Diese Woche' },
  { value: 'month', label: 'Dieser Monat' },
  { value: 'quarter', label: 'Dieses Quartal' },
];

export const SessionFilters = ({ filters, onFilterChange, activeFilterCount }: SessionFiltersProps) => {
  const handlePhaseChange = (value: string) => {
    onFilterChange({ ...filters, phase: value });
  };

  const handleAreaChange = (value: string) => {
    onFilterChange({ ...filters, decisionArea: value });
  };

  const handleDateChange = (value: string) => {
    onFilterChange({ ...filters, dateRange: value });
  };

  const handleReset = () => {
    onFilterChange({
      phase: 'all',
      decisionArea: 'all',
      dateRange: 'all',
      searchQuery: '',
    });
  };

  return (
    <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border border-border/50 bg-card p-3 sm:p-5 transition-all duration-500 hover:border-primary/20">
      {/* Background decorative element */}
      <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      
      <div className="relative z-10 space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center flex-shrink-0">
              <SlidersHorizontal className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-foreground">Filter</h3>
            {activeFilterCount > 0 && (
              <Badge className="h-4 sm:h-5 text-[10px] sm:text-xs bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15">
                {activeFilterCount}
              </Badge>
            )}
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-7 sm:h-8 text-[10px] sm:text-xs hover:bg-primary/10 hover:text-primary px-2 sm:px-3"
            >
              <X className="h-3 w-3 mr-0.5 sm:mr-1" />
              <span className="hidden xs:inline">Zurücksetzen</span>
              <span className="xs:hidden">Reset</span>
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          <div className="space-y-1.5 sm:space-y-2">
            <label className="text-xs sm:text-sm font-medium text-muted-foreground">Phase</label>
            <Select value={filters.phase} onValueChange={handlePhaseChange}>
              <SelectTrigger className="w-full h-9 sm:h-10 rounded-lg sm:rounded-xl border-border/50 bg-background hover:border-primary/30 transition-all text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-lg sm:rounded-xl border-border/50">
                {PHASE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="rounded-lg text-xs sm:text-sm">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <label className="text-xs sm:text-sm font-medium text-muted-foreground">Bereich</label>
            <Select value={filters.decisionArea} onValueChange={handleAreaChange}>
              <SelectTrigger className="w-full h-9 sm:h-10 rounded-lg sm:rounded-xl border-border/50 bg-background hover:border-primary/30 transition-all text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-lg sm:rounded-xl border-border/50">
                {AREA_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="rounded-lg text-xs sm:text-sm">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:space-y-2 xs:col-span-2 md:col-span-1">
            <label className="text-xs sm:text-sm font-medium text-muted-foreground">Zeitraum</label>
            <Select value={filters.dateRange} onValueChange={handleDateChange}>
              <SelectTrigger className="w-full h-9 sm:h-10 rounded-lg sm:rounded-xl border-border/50 bg-background hover:border-primary/30 transition-all text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-lg sm:rounded-xl border-border/50">
                {DATE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="rounded-lg text-xs sm:text-sm">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};
